import { useCSSVariables } from '@app/core/hooks/useCSSVariables';
import { useLocale } from '@app/core/hooks/useLocale';
import { cloneToMutableData } from '@app/shared/helpers';
import Highcharts from 'highcharts';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { abbreviatedValueFormatter, toPercentageUsingFormatter } from '@app/shared/formatters';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import {
  OpmDistributionPlotDto,
  OpmDistributionSummaryData,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { ChartZoomMode, ViewOption } from './opm-enums';
import styles from './opm-charts.module.scss';
import {
  axisLabelFormatter,
  formatNumber,
  percentageAxisLabelFormatter,
} from '../../shared/format-helpers';
import '@app/shared/helpers/charts/HighchartWrapper';
import { handleClashingLabels } from '@app/shared/helpers/charts/HandleClashingLabels';
import './chart-initialisation';
import { updateDistributionChartData } from './data-helpers';
import { destroyTooltip, renderTooltip } from './tooltip-helpers';
import { useAppSelector } from '@app/core/hooks/redux-hooks';

const TOOLTIP_HORIZONTAL_OFFSET_PX = 135;
const TOOLTIP_VERTICAL_OFFSET_PX = 10;

interface DistributionChartProps {
  selectedYear: string;
  selectedView: ViewOption | string;
  zoomMode: ChartZoomMode;
  zoomMinimum: number | undefined;
  zoomMaximum: number | undefined;
  chartDistribution: OpmDistributionPlotDto;
  isRiskNeutralChart: boolean;
  isEvSimulation: boolean;
  handleManualZoom: (min: number | undefined, max: number | undefined) => void;
}

export const DistributionChart: FC<DistributionChartProps> = ({
  selectedYear,
  selectedView,
  zoomMode,
  zoomMinimum,
  zoomMaximum,
  chartDistribution,
  isRiskNeutralChart,
  isEvSimulation,
  handleManualZoom,
}): JSX.Element => {
  const { l } = useLocale();
  const [chartRef, setChartRef] = useState<Highcharts.Chart | undefined>(undefined);
  const isInstrumentView = selectedView !== ViewOption.EV && selectedView !== ViewOption.Equity;
  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const currentPlotData = useMemo(() => {
    const chartData = cloneToMutableData(chartDistribution);
    if (isInstrumentView) {
      if (chartData.instruments[selectedView]) {
        chartData.instruments[selectedView] = updateDistributionChartData(
          chartData.instruments[selectedView],
          denomination
        );
      }
      return chartData?.instruments[selectedView] ?? [];
    }

    const data = selectedView === ViewOption.EV ? chartData?.enterpriseValue : chartData?.equity;
    return updateDistributionChartData(data, denomination) ?? [];
  }, [chartDistribution, isInstrumentView, selectedView, denomination]);

  const {
    colors: { colorSecondary, colorNeutralsB80, colorGrey400, colorNeutralsB90 },
    font: { fontFamilyPrimary },
  } = useCSSVariables();

  const getXaxisLabel = () => {
    if (isInstrumentView) {
      return isRiskNeutralChart ? l('_PVOfInstrument') : l('_InstrumentProceeds');
    }

    return selectedView === ViewOption.EV ? l('_EV') : l('_TotalAttributableEquity');
  };

  const getMean = (): { label: string; value: number; formattedValue: string } => {
    let mean = 0;
    const meanValues = currentPlotData[selectedYear].meanValue;
    switch (selectedView) {
      case ViewOption.EV:
        mean = Number(meanValues.simulatedValue);
        break;

      case ViewOption.Equity:
        mean = Number(isEvSimulation ? meanValues.totalEquityProceeds : meanValues.simulatedValue);
        break;

      default:
        mean = Number(
          isRiskNeutralChart ? meanValues.instrumentPresentValue : meanValues.instrumentProceeds
        );
        break;
    }

    return {
      label: isInstrumentView && isRiskNeutralChart ? l('_MPV') : l('_M'),
      value: mean,
      formattedValue: abbreviatedValueFormatter({ value: mean, fraction: 1 }),
    };
  };

  const getLowerQuartile = (): { label: string; value: number; formattedValue: string } => {
    const q1 = Number(currentPlotData[selectedYear].firstQuartile);
    return {
      label: isInstrumentView && isRiskNeutralChart ? l('_Q1PV') : l('_Q1'),
      value: q1,
      formattedValue: abbreviatedValueFormatter({ value: q1, fraction: 1 }),
    };
  };

  const getUpperQuartile = (): { label: string; value: number; formattedValue: string } => {
    const q3 = Number(currentPlotData[selectedYear].thirdQuartile);
    return {
      label: isInstrumentView && isRiskNeutralChart ? l('_Q3PV') : l('_Q3'),
      value: q3,
      formattedValue: abbreviatedValueFormatter({ value: q3, fraction: 1 }),
    };
  };

  const labelY = -30;
  const overlayColor = 'rgba(175, 175, 175, 0.5)';

  function tooltipFormatter(this: Highcharts.TooltipFormatterContextObject) {
    const category = this.point.category;
    const data = currentPlotData[selectedYear].dataPoints;
    const point = data.find((tt) => tt.x === category);
    const shouldShowInstrumentMoM = !isRiskNeutralChart && point?.instrumentMoM !== null;
    const shouldShowInstrumentIRR = !isRiskNeutralChart && point?.instrumentIRR !== null;
    const shouldShowInstrumentPresentValue = isInstrumentView;
    const formattedData = {
      simulatedValue: formatNumber(point?.simulatedValue, 0, true),
      totalEquity: formatNumber(point?.totalEquity, 0, true),
      totalShareholderDebt: formatNumber(point?.totalShareholderDebt, 0, true),
      totalOrdinaryEquity: formatNumber(point?.totalOrdinaryEquity, 0, true),
      institutionMoM: formatNumber(point?.institutionMoM),
      institutionIRR: toPercentageUsingFormatter(point?.institutionIRR ?? 0),
      instrumentMoM: formatNumber(point?.instrumentMoM),
      instrumentIRR: toPercentageUsingFormatter(point?.instrumentIRR ?? 0),
      instrumentPresentValue: formatNumber(point?.instrumentPresentValue, 0, true),
      instrumentProceeds: formatNumber(point?.instrumentProceeds, 0, true),
    };

    return `
    <div>
      <div class="${styles['flex']}">
      ${
        isEvSimulation
          ? `
          <div>
            <div class="${styles['w-150']}">${l('_EV')}</div>
            <div>
              <span><b>${formattedData?.simulatedValue}</b></span>
            </div>
          </div>
          `
          : ''
      }
        <div>
          <div class="${styles['w-150']}">${l('_TotalAttributableEquity')}</div>
          <div><b>${
            isEvSimulation ? formattedData?.totalEquity : formattedData?.simulatedValue
          }</b></div>
        </div>
      </div>
      <br/>
      <div class="${styles['flex']}">
        <div>
          <div class="${styles['w-150']}">${l('_ShareholderDebtTitle')}</div>
          <div><b>${formattedData?.totalShareholderDebt}</b></div>
        </div>
        <div>
          <div class="${styles['w-100']}">${l('_OrdinaryEquityTitle')}</div>
          <div><b>${formattedData?.totalOrdinaryEquity}</b></div>
        </div>
      </div>
      ${
        shouldShowInstrumentPresentValue
          ? `
          <br/>
          <div>
            <div class="${styles['w-100']}">${
              isRiskNeutralChart ? l('_InstrumentPV') : l('_InstrumentProceeds')
            }</div>
            <div><b>${
              isRiskNeutralChart
                ? formattedData?.instrumentPresentValue
                : formattedData?.instrumentProceeds
            }</b></div>
          </div>
        `
          : ''
      }
      ${
        !isRiskNeutralChart
          ? `
          <br/>
          <div class="${styles['flex']}">
            <div>
              <div class="${styles['w-150']}">${l('_SponsorMoM')}</div>
              <div><b>${formattedData?.institutionMoM}x</b></div>
            </div>
            <div>
              <div class="${styles['w-100']}">${l('_SponsorIRR')}</div>
              <div><b>${formattedData?.institutionIRR}</b></div>
            </div>
          </div>`
          : ''
      }
      ${
        shouldShowInstrumentMoM || shouldShowInstrumentIRR
          ? `
            <br/>
            <div class="${styles['flex']}">
              ${
                shouldShowInstrumentMoM
                  ? `
                    <div>
                      <div class="${styles['w-150']}">${l('_InstrumentMoM')}</div>
                      <div><b>${formattedData?.instrumentMoM}x</b></div>
                    </div>
                  `
                  : ''
              }
              ${
                shouldShowInstrumentIRR
                  ? `
                    <div>
                      <div class="${styles['w-100']}">${l('_InstrumentIRR')}</div>
                      <div><b>${formattedData?.instrumentIRR}</b></div>
                    </div>
                  `
                  : ''
              }
            </div>`
          : ''
      }
    </div>
  `;
  }

  const tooltipConfig: Highcharts.TooltipOptions = {
    useHTML: true,
    formatter: tooltipFormatter,
    backgroundColor: '#fff',
    borderColor: '#fff',
    borderRadius: 8,
    shadow: false,
    padding: 20,
    shared: true,
    outside: true,
    shape: 'square',
    positioner: function (labelWidth, labelHeight, point) {
      const chart = this.chart;
      const plotX = point.plotX + chart.plotLeft;
      const plotY = point.plotY;

      let tooltipX;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const barW = (this.chart.series[0] as any).barW;

      if (chart.series[0].data.length <= 1) {
        if (plotX < chart.plotWidth / 2) {
          tooltipX = plotX;
        } else {
          tooltipX = Math.min(plotX - labelWidth, chart.plotLeft + chart.plotWidth - labelWidth);
        }
        return {
          x: tooltipX,
          y: plotY,
        };
      }

      if (plotX < chart.plotWidth / 2) {
        tooltipX = Math.max(plotX + barW / 2, chart.plotLeft);
      } else {
        tooltipX = Math.min(
          plotX - labelWidth - barW / 2,
          chart.plotLeft + chart.plotWidth - labelWidth
        );
      }

      tooltipX = Math.min(tooltipX, chart.plotLeft + chart.plotWidth - labelWidth);

      tooltipX = Math.max(tooltipX, chart.plotLeft);

      const tooltipY = Math.max(
        Math.min(plotY - labelHeight + 40, chart.plotTop + chart.plotHeight - labelHeight),
        chart.plotTop + 5
      );

      return {
        x: tooltipX,
        y: tooltipY,
      };
    },
  };

  const displaySummaryTooltip = (
    x: number,
    y: number,
    values: OpmDistributionSummaryData,
    labels: {
      ev: string;
      totalAttributableEquity: string;
      shareholderDebt: string;
      ordinaryEquity: string;
      sponsorMoM: string;
      sponsorIRR: string;
      instrumentValue?: string;
      instrumentMoM?: string;
      instrumentIRR?: string;
    }
  ) => {
    const instrumentValueToCheck = isRiskNeutralChart
      ? values.instrumentPresentValue
      : values.instrumentProceeds;
    const shouldShowInstrumentValue =
      Boolean(labels.instrumentValue) && isInstrumentView && instrumentValueToCheck !== null;
    const shouldShowInstrumentMoM =
      Boolean(labels.instrumentMoM) && !isRiskNeutralChart && values.instrumentMoM !== null;
    const shouldShowInstrumentIRR =
      Boolean(labels.instrumentIRR) && !isRiskNeutralChart && values.instrumentIRR !== null;

    const formattedValues = {
      simulatedValue: formatNumber(Number(values.simulatedValue), 0, true),
      totalEquity: formatNumber(Number(values.totalEquityProceeds), 0, true),
      totalShareholderDebt: formatNumber(Number(values.totalShareholderDebt), 0, true),
      totalOrdinaryEquity: formatNumber(Number(values.totalOrdinaryEquity), 0, true),
      institutionMoM: formatNumber(Number(values.institutionMoM), 2),
      institutionIRR: toPercentageUsingFormatter(Number(values.institutionIRR)),
      instrumentMoM: formatNumber(Number(values.instrumentMoM), 2),
      instrumentIRR: toPercentageUsingFormatter(Number(values.instrumentIRR)),
      instrumentValue: formatNumber(
        Number(isRiskNeutralChart ? values.instrumentPresentValue : values.instrumentProceeds),
        0,
        true
      ),
    };

    const html = `<div style="white-space: nowrap;">
          <div class="${styles['flex']}">
          ${
            isEvSimulation
              ? `
              <div>
                <div class="${styles['w-200']}">${labels.ev}</div>
                <div>
                  <b>${formattedValues.simulatedValue}</b>
                </div>
              </div>`
              : ''
          }
              <div>
                <div class="${styles['w-200']}">${labels.totalAttributableEquity}</div>
                <div>
                  <b>${
                    isEvSimulation ? formattedValues.totalEquity : formattedValues.simulatedValue
                  }</b>
                </div>
              </div>
          </div>
          <br/>
          <div class="${styles['flex']}">
            <div>
              <div class="${styles['w-200']}">${labels.shareholderDebt}</div>
              <div>
                <b>${formattedValues.totalShareholderDebt}</b>
              </div>
            </div>
            <div>
              <div class="${styles['w-200']}">${labels.ordinaryEquity}</div>
              <div>
                <b>${formattedValues.totalOrdinaryEquity}</b>
              </div>
            </div>
          </div>
          ${
            shouldShowInstrumentValue
              ? `
                <br/>
                <div class="${styles['w-150']}">${labels.instrumentValue}</div>
                <div>
                  <b>${formattedValues.instrumentValue}</b>
                </div>`
              : ''
          }
          ${
            !isRiskNeutralChart
              ? `
                <br/>
                <div class="${styles['flex']}">
                  <div>
                    <div class="${styles['w-200']}">${labels.sponsorMoM}</div>
                    <div>
                      <b>${formattedValues.institutionMoM}x</b>
                    </div>
                  </div>
                  <div>
                    <div class="${styles['w-150']}">${labels.sponsorIRR}</div>
                    <div>
                      <b>${formattedValues.institutionIRR}</b>
                    </div>
                  </div>
                </div>`
              : ''
          }
          ${
            shouldShowInstrumentMoM || shouldShowInstrumentIRR
              ? `
                <br/>
                <div class="${styles['flex']}">
                ${
                  shouldShowInstrumentMoM
                    ? `
                      <div>
                        <div class="${styles['w-200']}">${labels.instrumentMoM}</div>
                        <div>
                          <b>${formattedValues.instrumentMoM}x</b>
                        </div>
                      </div>`
                    : ''
                }
                ${
                  shouldShowInstrumentIRR
                    ? `
                      <div>
                        <div class="${styles['w-150']}">${labels.instrumentIRR}</div>
                        <div>
                          <span><b>${formattedValues.instrumentIRR}</b></span>
                        </div>
                      </div>`
                    : ''
                }
                </div>`
              : ''
          }
        </div>
      `;

    renderTooltip({
      renderer: chartRef?.renderer,
      html,
      x,
      y,
      ref: tooltipElement,
    });
  };

  const buildTooltipLabels = (prefix: string) => ({
    ev: `${prefix} ${l('_EV')}`,
    totalAttributableEquity: `${prefix} ${l('_TotalAttributableEquity')}`,
    shareholderDebt: `${prefix} ${l('_ShareholderDebtTitle')}`,
    ordinaryEquity: `${prefix} ${l('_OrdinaryEquityTitle')}`,
    sponsorMoM: `${prefix} ${l('_SponsorMoM')}`,
    sponsorIRR: `${prefix} ${l('_SponsorIRR')}`,
    instrumentValue: `${prefix} ${
      isRiskNeutralChart ? l('_InstrumentPV') : l('_InstrumentProceeds')
    }`,
    instrumentMoM: `${prefix} ${l('_InstrumentMoM')}`,
    instrumentIRR: `${prefix} ${l('_InstrumentIRR')}`,
  });

  const selectedYearData = currentPlotData[selectedYear];
  const hasSelectedYearData = selectedYearData != null;

  const xAxisMinimum =
    zoomMode === ChartZoomMode.Zoomed
      ? zoomMinimum
      : hasSelectedYearData
      ? Number(selectedYearData.defaultViewMinimum)
      : undefined;
  const xAxisMaximum =
    zoomMode === ChartZoomMode.Zoomed
      ? zoomMaximum
      : hasSelectedYearData
      ? Number(selectedYearData.defaultViewMaximum)
      : undefined;

  const tooltipElement = useRef<Highcharts.SVGElement | undefined>(undefined);
  const hoveredTooltipIdRef = useRef<string>('');
  const eventListener = useRef<null | EventListener>(null);
  const mouseLeaveListener = useRef<null | EventListener>(null);
  const chartOptions = useMemo<Highcharts.Options>(
    () => {
      const hasData = hasSelectedYearData;
      const mean = hasData ? getMean() : null;
      const lowerQuartile = hasData ? getLowerQuartile() : null;
      const upperQuartile = hasData ? getUpperQuartile() : null;
      return {
        chart: {
          events: {
            load: function () {
              handleClashingLabels(this.xAxis);
            },
            redraw: function () {
              handleClashingLabels(this.xAxis);
            },
          },
          marginTop: 60,
          marginBottom: 100,
          marginRight: 52,
          animation: true,
          type: 'column',
          zooming: {
            type: 'x',
          },
          reflow: true,
          style: { fontFamily: fontFamilyPrimary },
        },
        tooltip: tooltipConfig,
        credits: {
          enabled: false,
        },
        title: {
          text: undefined,
        },
        xAxis: {
          tickLength: 0,
          title: {
            text: getXaxisLabel(),
            style: {
              color: colorNeutralsB80,
              fontSize: '1.4rem',
              fontWeight: '700',
            },
            y: 30,
          },
          min: 0,
          endOnTick: true,
          labels: {
            style: {
              color: colorNeutralsB80,
              fontSize: '1.4rem',
              fontWeight: '500',
            },
            formatter: axisLabelFormatter,
            y: 30,
          },
          events: {
            setExtremes: function (e) {
              if (e.trigger === 'zoom') {
                handleManualZoom(e.min, e.max);
              }
            },
          },
          plotBands:
            hasData && isRiskNeutralChart
              ? [
                  {
                    id: 'collaringLow',
                    from: 0,
                    to: currentPlotData[selectedYear].collaringFloor ?? undefined,
                    color: overlayColor,
                    zIndex: 1,
                    label: {
                      align: 'left',
                      y: labelY,
                      rotation: 0,
                      formatter: () => {
                        return `<tspan dy="0" style="color: ${colorGrey400}; font-size: 14px">${l(
                          '_Floor'
                        )}</tspan><br><tspan dy="2" style="color: ${colorNeutralsB90}; font-size: 14px">${toPercentageUsingFormatter(
                          currentPlotData[selectedYear].collaringPercentage
                        )}</tspan>`;
                      },
                    },
                  },
                  {
                    id: 'collaringHigh',
                    from: currentPlotData[selectedYear].collaringCeiling ?? undefined,
                    to: Number.MAX_VALUE,
                    color: overlayColor,
                    zIndex: 1,
                    label: {
                      align: 'right',
                      y: labelY,
                      rotation: 0,
                      formatter: () => {
                        return `<tspan dy="0" style="color: ${colorGrey400}; font-size: 14px">${l(
                          '_Cap'
                        )}</tspan><br><tspan dy="2" style="color: ${colorNeutralsB90}; font-size: 14px">${toPercentageUsingFormatter(
                          currentPlotData[selectedYear].collaringPercentage
                        )}</tspan>`;
                      },
                    },
                  },
                ]
              : [],
          plotLines: hasData
            ? [
                {
                  id: 'mean',
                  label: {
                    id: 'mean-tooltip',
                    align: 'center',
                    y: labelY,
                    rotation: 0,
                    formatter: () => {
                      return `<tspan dy="0" id="mean-tooltip-label" style="color: ${colorNeutralsB80}; font-size: 14px;">${
                        mean!.label
                      }</tspan><br><tspan dy="2" id="mean-tooltip-value" style="color: ${colorNeutralsB90}; font-size: 14px;">${
                        mean!.formattedValue
                      }</tspan>`;
                    },
                  },
                  value: mean!.value,
                  zIndex: 1,
                  width: 2,
                  color: colorGrey400,
                },
                {
                  id: 'firstQuartile',
                  label: {
                    id: 'q1-tooltip',
                    align: 'center',
                    y: labelY,
                    x: 0,
                    rotation: 0,
                    formatter: () => {
                      return `<tspan dy="0" id="q1-tooltip-label" style="color: ${colorNeutralsB80}; font-size: 14px;">${
                        lowerQuartile!.label
                      }</tspan><br><tspan dy="2" id="q1-tooltip-value" style="color: ${colorNeutralsB90}; font-size: 14px;">${
                        lowerQuartile!.formattedValue
                      }</tspan>`;
                    },
                  },
                  value: lowerQuartile!.value,
                  zIndex: 1,
                  width: 2,
                  color: colorGrey400,
                  style: {
                    overflow: 'none',
                    crop: false,
                  },
                },
                {
                  id: 'thirdQuartile',
                  label: {
                    id: 'q3-tooltip',
                    align: 'center',
                    y: labelY,
                    x: 0,
                    rotation: 0,
                    formatter: () => {
                      return `<tspan dy="0" id="q3-tooltip-label" style="color: ${colorNeutralsB80}; font-size: 14px;">${
                        upperQuartile!.label
                      }</tspan><br><tspan dy="2" id="q3-tooltip-value" style="color: ${colorNeutralsB90}; font-size: 14px;">${
                        upperQuartile!.formattedValue
                      }</tspan>`;
                    },
                  },
                  value: upperQuartile!.value,
                  zIndex: 1,
                  width: 2,
                  color: colorGrey400,
                },
              ]
            : [],
        },
        yAxis: {
          ceiling: 100,
          title: {
            text: l('_PercentageSimulations'),
            style: {
              color: colorNeutralsB80,
              fontSize: '1.4rem',
              fontWeight: '700',
            },
            x: -10,
          },
          labels: {
            style: {
              color: colorNeutralsB80,
              fontSize: '1.4rem',
              fontWeight: '500',
            },
            formatter: percentageAxisLabelFormatter,
          },
        },
        plotOptions: {
          column: {
            pointPadding: 0,
            borderWidth: 1,
            groupPadding: 0,
            stickyTracking: true,
            shadow: false,
            states: {
              hover: {
                brightness: 0.3,
              },
            },
          },
        },
        series: [
          {
            id: `${selectedView}-${selectedYear}`,
            type: 'column',
            data: hasData
              ? currentPlotData[selectedYear].dataPoints.map((item) => ({
                  ...item,
                  color: item.instrumentPresentValue === 0 ? colorNeutralsB80 : colorSecondary,
                }))
              : [],
            pointPlacement: 'between',
          },
        ],
        legend: {
          enabled: false,
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CSS variables from useCSSVariables are stable
    [
      currentPlotData,
      selectedYear,
      selectedView,
      isRiskNeutralChart,
      isEvSimulation,
      hasSelectedYearData,
      l,
    ]
  );

  useEffect(() => {
    // handle zoom
    if (chartRef && chartRef.xAxis) {
      const min = zoomMode === ChartZoomMode.AllData ? 0 : xAxisMinimum;
      const max = zoomMode === ChartZoomMode.AllData ? undefined : xAxisMaximum;
      chartRef.xAxis[0].setExtremes(min, max);
    }
  }, [chartRef, zoomMode, xAxisMaximum, xAxisMinimum]);

  useEffect(() => {
    if (!chartRef?.renderer) {
      return;
    }

    const container = chartRef.container;

    if (eventListener.current) {
      container.removeEventListener('mousemove', eventListener.current);
    }

    if (mouseLeaveListener.current) {
      container.removeEventListener('mouseleave', mouseLeaveListener.current);
    }

    eventListener.current = (event: Event) => {
      const mouseEvent = event as MouseEvent;
      const rect = container.getBoundingClientRect();
      const x = mouseEvent.clientX - rect.left - TOOLTIP_HORIZONTAL_OFFSET_PX;
      const y = mouseEvent.clientY - rect.top + TOOLTIP_VERTICAL_OFFSET_PX;
      const target = event.target;
      if (!(target instanceof Element)) {
        destroyTooltip(tooltipElement);
        return;
      }
      const tooltipId =
        target.id === 'mean-tooltip-label' ||
        target.id === 'mean-tooltip-value' ||
        target.id === 'mean-tooltip'
          ? 'mean-tooltip'
          : target.id === 'q1-tooltip-label' ||
            target.id === 'q1-tooltip-value' ||
            target.id === 'q1-tooltip'
          ? 'q1-tooltip'
          : target.id === 'q3-tooltip-label' ||
            target.id === 'q3-tooltip-value' ||
            target.id === 'q3-tooltip'
          ? 'q3-tooltip'
          : '';

      if (!tooltipId || !selectedYearData) {
        destroyTooltip(tooltipElement);
        hoveredTooltipIdRef.current = '';
        return;
      }

      if (tooltipId === hoveredTooltipIdRef.current) {
        return;
      }

      hoveredTooltipIdRef.current = tooltipId;

      if (tooltipId === 'mean-tooltip') {
        displaySummaryTooltip(x, y, selectedYearData.meanValue, buildTooltipLabels(l('_Mean')));
      } else if (tooltipId === 'q1-tooltip' && selectedYearData.firstQuartileValue) {
        displaySummaryTooltip(
          x,
          y,
          selectedYearData.firstQuartileValue,
          buildTooltipLabels(l('_Q1'))
        );
      } else if (tooltipId === 'q3-tooltip' && selectedYearData.thirdQuartileValue) {
        displaySummaryTooltip(
          x,
          y,
          selectedYearData.thirdQuartileValue,
          buildTooltipLabels(l('_Q3'))
        );
      } else {
        destroyTooltip(tooltipElement);
        hoveredTooltipIdRef.current = '';
      }
    };

    mouseLeaveListener.current = () => {
      destroyTooltip(tooltipElement);
      hoveredTooltipIdRef.current = '';
    };

    container.addEventListener('mousemove', eventListener.current);
    container.addEventListener('mouseleave', mouseLeaveListener.current);

    return () => {
      if (eventListener.current) {
        container.removeEventListener('mousemove', eventListener.current);
      }
      if (mouseLeaveListener.current) {
        container.removeEventListener('mouseleave', mouseLeaveListener.current);
      }
      destroyTooltip(tooltipElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- displaySummaryTooltip and buildTooltipLabels are recreated every render; their actual state deps are already listed
  }, [
    chartRef?.renderer,
    selectedView,
    selectedYear,
    currentPlotData,
    isRiskNeutralChart,
    isEvSimulation,
    l,
  ]);

  const chartRefCallback = useCallback((chartContainer: HighchartsReactRefObject) => {
    if (chartContainer != null) {
      setChartRef(chartContainer.chart);
    }
  }, []);

  return (
    <>
      {selectedYear && currentPlotData && (
        <HighchartsReact ref={chartRefCallback} highcharts={Highcharts} options={chartOptions} />
      )}
    </>
  );
};
